import React, { useEffect, useState } from 'react';
import { Archive, CreditCard, LogOut, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  getStoreId,
  getSettings,
  setApiKey,
  setSettings as persistSettings,
} from '../utils/storage';
import { apiFetch } from '../utils/api';
import { toastError, toastSuccess } from '../utils/toast';
import Vault from './Vault';
import Upgrade from './Upgrade';
import Loading from './Loading';
import StatusBadge from './StatusBadge';

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'fr', label: 'FR', name: 'Français' },
  { code: 'es', label: 'ES', name: 'Español' },
  { code: 'it', label: 'IT', name: 'Italiano' },
];

export default function MainApp({ storeInfo, onLogout }) {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('vault');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [hasAutoConnected, setHasAutoConnected] = useState(false);
  const [statusRefresh, setStatusRefresh] = useState(0);

  useEffect(() => {
    if (typeof window.EcwidApp === 'undefined' || window.self === window.top) return;
    window.EcwidApp.setSize({ height: 1400 });
  }, [activeTab]);

  useEffect(() => {
    async function loadSettings() {
      const hashSiteId = getStoreId();
      if (!hashSiteId) {
        setSettings({});
        setIsLoadingSettings(false);
        return;
      }

      const stored = getSettings();
      if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) {
        setSettings(stored);
        setIsLoadingSettings(false);
        return;
      }

      try {
        const data = await apiFetch('/api/settings', {}, { id: hashSiteId });
        const next = data.settings || {};
        setSettings(next);
        persistSettings(next);
        if (next.apiKey || data.apiKey) setApiKey(next.apiKey || data.apiKey);
      } catch (err) {
        toastError(err, t('mainApp.couldNotLoadSettings'), 'settings');
        setSettings({});
      } finally {
        setIsLoadingSettings(false);
      }
    }
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot load
  }, []);

  useEffect(() => {
    if (isLoadingSettings || !settings || settings.apiKey || hasAutoConnected) return;

    const hashSiteId = getStoreId();
    if (!hashSiteId) return;

    setHasAutoConnected(true);

    apiFetch('/api/register', { method: 'POST' }, { id: hashSiteId })
      .then((data) => {
        const next = { ...(settings || {}), apiKey: data.api_key };
        if (data.trial_ends_at) next.trial_ends_at = data.trial_ends_at;
        setSettings(next);
        persistSettings(next);
        setApiKey(data.api_key);
        setStatusRefresh((n) => n + 1);
        toastSuccess(t('mainApp.connectedToast'));
      })
      .catch((err) => {
        toastError(err, t('mainApp.couldNotConnect'), 'register');
        setHasAutoConnected(false);
      });
  }, [isLoadingSettings, settings, hasAutoConnected, t]);

  const connecting = hasAutoConnected && !settings?.apiKey;
  const showLoading = isLoadingSettings || connecting;
  const storeUrl = storeInfo?.profile?.generalInfo?.storeUrl;
  const storeName = storeInfo?.profile?.generalInfo?.storeName;
  const activeLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

  function setLanguage(code) {
    if (!code || code === activeLang) return;
    void i18n.changeLanguage(code);
  }

  return (
    <div className="main-app">
      <header className="mobile-topbar d-md-none">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label={sidebarOpen ? t('mainApp.closeMenu') : t('mainApp.openMenu')}
          aria-expanded={sidebarOpen}
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <img
          className="mobile-topbar-logo"
          src="/backuply-logo-long.png"
          alt="Backuply"
          width={120}
          height={26}
        />
        <span className="mobile-topbar-spacer" aria-hidden="true" />
      </header>

      {sidebarOpen ? (
        <button
          type="button"
          className="sidebar-backdrop d-md-none"
          aria-label={t('mainApp.closeMenu')}
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} aria-label={t('mainApp.mainNav')}>
        <div className="sidebar-brand">
          <img
            className="sidebar-logo"
            src="/backuply-logo-long.png"
            alt="Backuply"
            width={160}
            height={34}
          />
          <span className="sidebar-brand-sub">{t('mainApp.forEcwid')}</span>
        </div>
        <nav className="sidebar-nav" aria-label={t('mainApp.primary')}>
          <div className="sidebar-nav-label">{t('mainApp.workspace')}</div>
          <button
            type="button"
            className={activeTab === 'vault' ? 'active' : ''}
            onClick={() => {
              setActiveTab('vault');
              setSidebarOpen(false);
            }}
          >
            <Archive size={18} /> {t('mainApp.vault')}
          </button>
          <button
            type="button"
            className={activeTab === 'upgrade' ? 'active' : ''}
            onClick={() => {
              setActiveTab('upgrade');
              setSidebarOpen(false);
            }}
          >
            <CreditCard size={18} /> {t('mainApp.upgrade')}
          </button>
        </nav>

        <div className="sidebar-language" role="group" aria-label={t('mainApp.language')}>
          <div className="sidebar-language-label">{t('mainApp.language')}</div>
          <div className="sidebar-language-buttons">
            {LANGUAGE_OPTIONS.map(({ code, label, name }) => (
              <button
                key={code}
                type="button"
                className={`sidebar-language-btn ${activeLang === code ? 'active' : ''}`}
                onClick={() => setLanguage(code)}
                title={name}
                aria-pressed={activeLang === code}
                aria-label={name}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="sidebar-footer">
          {storeName || storeUrl ? (
            <div className="sidebar-store">
              <div className="sidebar-store-name text-truncate">{storeName || storeUrl}</div>
              {storeName && storeUrl ? (
                <div className="sidebar-store-url text-truncate">{storeUrl}</div>
              ) : null}
              {settings?.apiKey ? (
                <StatusBadge tone="success">{t('mainApp.connected')}</StatusBadge>
              ) : null}
            </div>
          ) : null}
          <button type="button" className="logout-btn" onClick={onLogout}>
            <LogOut size={16} /> {t('mainApp.reinstallOauth')}
          </button>
        </div>
      </aside>

      <main className="main-content">
        {showLoading ? (
          <Loading
            message={connecting ? t('mainApp.connecting') : t('mainApp.loading')}
          />
        ) : activeTab === 'upgrade' ? (
          <Upgrade onGoVault={() => setActiveTab('vault')} />
        ) : (
          <Vault refreshKey={statusRefresh} onUpgrade={() => setActiveTab('upgrade')} />
        )}
      </main>
    </div>
  );
}
